import type { Capture, GameState, Piece, Side, Square } from "./types";
import { canShapeReach } from "./moves";
import { fromKey, inBounds, squareKey } from "./coords";

interface PiecePlacement {
  piece: Piece;
  square: Square;
}

function listPieces(state: GameState, side: Side): PiecePlacement[] {
  const out: PiecePlacement[] = [];
  for (const [key, piece] of state.board) {
    if (piece.side === side) out.push({ piece, square: fromKey(key) });
  }
  return out;
}

/**
 * Can this piece reach `to` from its current square, treating `to` as the destination
 * regardless of its contents? For Pyramids, true if any movement mode reaches.
 *
 * Uses `forCapture: true` so non-capturing flying moves (Triangle's knight-hop, Square's
 * extended-knight hop) don't count — historical Rithmomachia rules forbid using the flying
 * move to capture an enemy. Reach for movement is more permissive; that's a separate query.
 */
export function canPieceReach(state: GameState, p: PiecePlacement, to: Square): boolean {
  const { piece, square } = p;
  const opts = { forCapture: true };
  if (piece.shape === "pyramid") {
    return (
      canShapeReach(state, "round", square, to, opts) ||
      canShapeReach(state, "triangle", square, to, opts) ||
      canShapeReach(state, "square", square, to, opts)
    );
  }
  return canShapeReach(state, piece.shape, square, to, opts);
}

/** All Encounter captures currently available to `side`. */
export function findEncounters(state: GameState, side: Side): Capture[] {
  const captures: Capture[] = [];
  const myPieces = listPieces(state, side);
  const enemy = listPieces(state, side === "white" ? "black" : "white");

  for (const me of myPieces) {
    for (const foe of enemy) {
      if (!canPieceReach(state, me, foe.square)) continue;

      // Whole-piece equality (works for Pyramids too — piece.value is the live sum).
      if (me.piece.value === foe.piece.value) {
        captures.push({
          method: "encounter",
          target: foe.square,
          attackers: [me.square],
          formula: `${me.piece.value} = ${foe.piece.value}`,
          advanceTo: foe.square,
        });
      }

      // Layer-by-layer equality against an enemy Pyramid (Fulke V1: "by partes").
      if (foe.piece.shape === "pyramid" && foe.piece.layers) {
        for (let i = 0; i < foe.piece.layers.length; i++) {
          const layer = foe.piece.layers[i];
          if (layer.captured) continue;
          if (me.piece.value !== layer.value) continue;
          // Skip if it duplicates the whole-piece capture (live value coincidentally equals
          // a single uncaptured layer value).
          if (me.piece.value === foe.piece.value) continue;
          captures.push({
            method: "encounter",
            target: foe.square,
            attackers: [me.square],
            formula: `${me.piece.value} = layer ${layer.value}`,
            advanceTo: foe.square,
            pyramidLayer: { layerIndex: i, layerValue: layer.value, layerShape: layer.shape },
          });
        }
      }
    }
  }
  return captures;
}

/**
 * All Assault (Ambush) captures currently available to `side`.
 * Fulke V1 supports Addition, Subtraction, Multiplication, and Division (integer-exact).
 * Also scans Pyramid layers: a layer can be captured if two attackers compose to its value.
 */
export function findAssaults(state: GameState, side: Side): Capture[] {
  const captures: Capture[] = [];
  const myPieces = listPieces(state, side);
  const enemy = listPieces(state, side === "white" ? "black" : "white");

  function matchPairAgainst(va: number, vb: number, tv: number): Array<{ formula: string }> {
    const matches: Array<{ formula: string }> = [];
    if (va + vb === tv) matches.push({ formula: `${va} + ${vb} = ${tv}` });
    if (Math.abs(va - vb) === tv) {
      matches.push({ formula: `${Math.max(va, vb)} - ${Math.min(va, vb)} = ${tv}` });
    }
    if (va * vb === tv) matches.push({ formula: `${va} × ${vb} = ${tv}` });
    // Division: integer-exact only. Either va/vb or vb/va must equal tv.
    if (vb !== 0 && va % vb === 0 && va / vb === tv) {
      matches.push({ formula: `${va} ÷ ${vb} = ${tv}` });
    }
    if (va !== 0 && vb % va === 0 && vb / va === tv) {
      matches.push({ formula: `${vb} ÷ ${va} = ${tv}` });
    }
    return matches;
  }

  for (const foe of enemy) {
    // Pieces that can reach this enemy square
    const reachers: PiecePlacement[] = myPieces.filter((m) => canPieceReach(state, m, foe.square));
    if (reachers.length < 2) continue;

    const seen = new Set<string>();
    for (let i = 0; i < reachers.length; i++) {
      for (let j = i + 1; j < reachers.length; j++) {
        const a = reachers[i];
        const b = reachers[j];
        const va = a.piece.value;
        const vb = b.piece.value;

        // Whole-piece target.
        for (const m of matchPairAgainst(va, vb, foe.piece.value)) {
          const k = `${squareKey(foe.square)}|${squareKey(a.square)}|${squareKey(b.square)}|${m.formula}`;
          if (seen.has(k)) continue;
          seen.add(k);
          captures.push({
            method: "assault",
            target: foe.square,
            attackers: [a.square, b.square],
            formula: m.formula,
          });
        }

        // Layer-by-layer target (Pyramid only).
        if (foe.piece.shape === "pyramid" && foe.piece.layers) {
          for (let li = 0; li < foe.piece.layers.length; li++) {
            const layer = foe.piece.layers[li];
            if (layer.captured) continue;
            if (layer.value === foe.piece.value) continue; // already covered as whole-piece
            for (const m of matchPairAgainst(va, vb, layer.value)) {
              const k = `${squareKey(foe.square)}|${squareKey(a.square)}|${squareKey(b.square)}|L${li}|${m.formula}`;
              if (seen.has(k)) continue;
              seen.add(k);
              captures.push({
                method: "assault",
                target: foe.square,
                attackers: [a.square, b.square],
                formula: `${m.formula} (layer)`,
                pyramidLayer: { layerIndex: li, layerValue: layer.value, layerShape: layer.shape },
              });
            }
          }
        }
      }
    }
  }
  return captures;
}

/** All Siege captures currently available to `side`. Enemy surrounded on all 4 orthogonal sides by friendly pieces or board edges. */
export function findSieges(state: GameState, side: Side): Capture[] {
  const captures: Capture[] = [];
  const enemy = listPieces(state, side === "white" ? "black" : "white");

  for (const foe of enemy) {
    const sides: Array<readonly [number, number]> = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ];
    const surrounders: Square[] = [];
    let allBlocked = true;

    for (const [df, dr] of sides) {
      const adj: Square = { file: foe.square.file + df, rank: foe.square.rank + dr };
      if (!inBounds(adj)) continue; // board edge counts as wall
      const occ = state.board.get(squareKey(adj));
      if (occ && occ.side === side) {
        surrounders.push(adj);
        continue;
      }
      // Not blocked from this side
      allBlocked = false;
      break;
    }

    if (allBlocked) {
      captures.push({
        method: "siege",
        target: foe.square,
        attackers: surrounders,
        formula: `siege (${surrounders.length} attacker${surrounders.length === 1 ? "" : "s"} + ${4 - surrounders.length} edge${4 - surrounders.length === 1 ? "" : "s"})`,
      });
    }
  }
  return captures;
}

/**
 * In v1, Deceit is treated as identical to Encounter (mandatory advance is offered as the same option).
 * The function is here so the rule variation can be enabled later without restructuring the engine.
 */
export function findDeceits(state: GameState, side: Side): Capture[] {
  // Same scan as encounters, returned with method: "deceit" if the caller wants the strict variant.
  const out: Capture[] = [];
  for (const c of findEncounters(state, side)) {
    out.push({ ...c, method: "deceit" });
  }
  return out;
}

/** Aggregate of all available captures (sieges + non-sieges). */
export function findAllCaptures(state: GameState, side: Side): {
  sieges: Capture[];
  encounters: Capture[];
  assaults: Capture[];
} {
  return {
    sieges: findSieges(state, side),
    encounters: findEncounters(state, side),
    assaults: findAssaults(state, side),
  };
}
